import { RoundButton } from '@/components/buttons/Buttons';
import { Checkbox, type CheckboxChangeEvent } from 'primereact/checkbox';
import { Sidebar, type SidebarProps } from 'primereact/sidebar';
import { classNames } from 'primereact/utils';
import style from '../../views/employee.module.css';
import { cn } from '@/components/ui/cn';
import { XpressVehicleType } from '@/modules/vehicle/engine/VehicleType';
import { PAYMENT_STATUSES_UI } from '@/modules/vehicle/engine/PaymentStatus';

interface Props extends SidebarProps {
	typeFilter: XpressVehicleType[];
	setTypeFilter: React.Dispatch<React.SetStateAction<XpressVehicleType[]>>;
	statusFilter: string[];
	setStatusFilter: React.Dispatch<React.SetStateAction<string[]>>;
	onFilterClear: () => void;
	type: string;
}

type Option = {
	label: string;
	value: string;
};

type VehicleTypeOption = {
	label: string;
	value: XpressVehicleType;
};

const Filters = (props: Props) => {
	const { onHide, typeFilter, setTypeFilter, statusFilter, setStatusFilter, onFilterClear, type, ...rest } = props;
	const transactionTypes: VehicleTypeOption[] = [
		{
			label: 'Ride - Moto',
			value: XpressVehicleType.Moto,
		},
		{
			label: 'Ride - Sedan',
			value: XpressVehicleType.Sedan,
		},
		{
			label: 'Ride - SUV',
			value: XpressVehicleType.SUV,
		},
	];

	// Finance options come from PAYMENT_STATUSES_UI so adding a future user-facing
	// status is a one-line change. RESERVED is intentionally not exposed here; it
	// is folded into "Pending" by the payment status helper.
	const transactionStatus: Option[] =
		type === 'finance'
			? PAYMENT_STATUSES_UI.map((s) => ({ label: s, value: s }))
			: [
				{
					label: 'Completed',
					value: 'Completed',
				},
				{
					label: 'Cancelled',
					value: 'Cancelled',
				},
			];

	const isTypeChecked = (value: XpressVehicleType) => typeFilter.includes(value);

	const isStatusChecked = (value: string) => statusFilter.includes(value);

	const handleTypeChange = (e: CheckboxChangeEvent) => {
		const vehicleType = e.value as XpressVehicleType;
		if (e.checked) {
			setTypeFilter([...typeFilter, vehicleType]);
		} else {
			setTypeFilter(typeFilter.filter((v) => v !== vehicleType));
		}
	};

	const handleStatusChange = (e: CheckboxChangeEvent) => {
		if (e.checked) {
			setStatusFilter([...statusFilter, e.value]);
		} else {
			setStatusFilter(statusFilter.filter(v => v !== e.value));
		}
	};

	const headerTemplate = (
		<h1 className="text-headline-small text-[28px] font-semibold mt-3">
			Filter
		</h1>
	);

	const footerTemplate = (
		<div className={classNames(style.employeeFilterFooter)}>
			<RoundButton
				label="Apply"
				id={'apply-filter'}
				onClick={onHide}
				className={cn(style.employeeFilterButton, 'with-icon-button-primary')}
			/>
			<RoundButton
				label="Cancel"
				id={'cancel-apply-filter'}
				onClick={() => {
					onFilterClear()
					onHide();
				}}
				className={classNames(
					style.employeeCancelButton,
					style.employeeFilterButton,
				)}
			/>
		</div>
	);

	return (
		<Sidebar
			{...rest}
			onHide={onHide}
			position="right"
			header={headerTemplate}
			pt={{
				root: () => ({
					className: classNames(
						style.employeeSideSheetTop,
						style.employeeSideSheetBottom,
						style.employeeSideSheet,
						style.employeeFilterRoot,
					),
				}),
				header: {
					className: classNames(
						style.employeeSideSheetHeader,
						style.employeeSideSheetTop,
						style.employeeSideSheet,
					),
				},
				content: {
					className: classNames(
						style.employeeFilterContent,
						style.employeeSideSheetBottom,
					),
				},
			}}
		>
			<div className="flex flex-col h-full justify-between">
				<div className="space-y-3">
					<div className="px-6">
						<h1 className="text-title-medium font-semibold py-1">
							Transaction Type
						</h1>
						<div>
							{transactionTypes.map((type) => (
								<div
									className="space-x-4 py-3 text-onSurface flex items-center"
									key={type.value}
								>
									<Checkbox
										pt={{
											root: {
												className:
													'hover:bg-transparent',
											},
											box: {
												className: classNames(
													style.employeeCheckboxInput,
													{
														[style.employeeCheckboxInputChecked]:
															isTypeChecked(
																type.value,
															),
													},
												),
											},
											icon: {
												className:
													style.employeeCheckboxIcon,
											},
										}}
										checked={isTypeChecked(type.value)}
										value={type.value}
										onChange={handleTypeChange}
									/>
									<span>{type.label}</span>
								</div>
							))}
						</div>
					</div>
					<div className="px-6">
						<h1 className="text-title-medium font-semibold py-1">
							Status
						</h1>
						<div>
							{transactionStatus.map((type) => (
								<div
									className="space-x-4 py-3 text-onSurface flex items-center"
									key={type.value}
								>
									<Checkbox
										pt={{
											root: {
												className:
													'hover:bg-transparent',
											},
											box: {
												className: classNames(
													style.employeeCheckboxInput,
													{
														[style.employeeCheckboxInputChecked]:
															isStatusChecked(
																type.value,
															),
													},
												),
											},
											icon: {
												className:
													style.employeeCheckboxIcon,
											},
										}}
										checked={isStatusChecked(type.value)}
										value={type.value}
										onChange={handleStatusChange}
									/>
									<span>{type.label}</span>
								</div>
							))}
						</div>
					</div>
				</div>

				{footerTemplate}
			</div>
		</Sidebar>
	);
};

export default Filters;
